/**
 * Service for fetching product option definitions used by the
 * InsyteQueryConfigOptionPredicateCtrl filter UI.
 */
import { getUserProductList, loadProductModel, optionDisplayTypes, configFrom } from '../../services/ProductService';

/**
 * Returns active products at their active version for the option filter picker.
 * Shape: [{ ID: guid, Name: string }]
 */
export async function getQueryProductList({ supplierKey } = {}) {
    const products = getUserProductList({
        includeExclusions: true,
        excludeEmpty: true,
        filterBySupplierID: supplierKey
    });
    console.log('[InsyteQueryProductOptionService] getQueryProductList:', products?.length, 'products');
    return (products ?? []).map(p => ({
        ID: p.ID,
        Name: p.Product ?? p.Supplier ?? p.ID,
        SupplierKey: p.SupplierKey,
        Supplier: p.Supplier
    }));
}

export async function getQueryProductSizeConfig(productId) {
    if (!productId) {
        return { allowWidth: false, allowDrop: false };
    }

    try {
        const model = await loadProductModel(productId);
        const productSize = model?.GetSize?.();
        return {
            allowWidth: productSize?.WidthMeasurementType === window.InsyteConfig.ProductSize_WidthMeasurementTypeValues.Single,
            allowDrop: productSize?.HeightMeasurementType === window.InsyteConfig.ProductSize_HeightMeasurementTypeValues.Single
        };
    } catch (e) {
        console.error('[InsyteQueryProductOptionService] getQueryProductSizeConfig threw for product', productId, e);
        return { allowWidth: false, allowDrop: false };
    }
}

/**
 * Maps an InsyteConfig DisplayType to the simpler type token used by the predicate ctrl.
 */
function mapDisplayType(displayType) {
    switch (displayType) {
        case optionDisplayTypes.Picklist:    return 'Picklist';
        case optionDisplayTypes.Numeric:
        case optionDisplayTypes.Decimal:
        case optionDisplayTypes.Length:     return 'Numeric';
        case optionDisplayTypes.Checkbox:   return 'Boolean';
        default:                            return 'Free';
    }
}

/**
 * Returns option groups + options for a given product using the loaded product model.
 * Shape: [{ groupName: string, options: [{ optionID: guid, optionName: string, optionType: string, values: [{id, label}] }] }]
 */
export async function getQueryProductOptions(productId, { showValueCode = false } = {}) {
    if (!productId) return [];

    console.log('[InsyteQueryProductOptionService] getQueryProductOptions called for', productId);

    try {
        const model = await loadProductModel(productId);
        console.log('[InsyteQueryProductOptionService] loadProductModel result:', model ? 'OK' : 'NULL');
        if (!model) return [];

        const groups = model.GetProductOptionGroups().toArray().filter(g => g.Active);

        return groups.map(group => {
            // GetProductOptions (all options for a group) wrapped with configFrom for Bridge.NET IEnumerable
            const rawOptions = configFrom(model.GetProductOptions(group.ID))
                .where(o => o.Active)
                .toArray();

            const options = rawOptions.map(option => {
                const displayType = option.DisplayType;
                const optionType = mapDisplayType(displayType);

                // For picklist options, get values from the DataModel via GetProductOptionValues
                const values = optionType === 'Picklist'
                    ? configFrom(model.GetProductOptionValues(option.ID))
                        .where(v => v.Active)
                        .select(v => ({
                            id: v.ID?.toString(),
                            label: showValueCode && !`${v.Code ?? ''}`.trim().length
                                ? v.Description
                                : showValueCode
                                    ? `${v.Description} (${v.Code})`
                                    : v.Description
                        }))
                        .toArray()
                    : [];

                return {
                    optionID:   option.ID?.toString(),
                    optionName: option.Description,
                    optionType,
                    values
                };
            });

            return {
                groupName: group.Description,
                options
            };
        }).filter(g => g.options.length > 0);

    } catch (e) {
        console.error('[InsyteQueryProductOptionService] getQueryProductOptions threw for product', productId, e);
        return [];
    }
}
